import React, { useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
    compName_character,
    livePortsNumber,
} from '../logic/panels/helpers/LiveButton'

/**
 * LiveTriggerButtonControl – Momentary push button for PlayScreen.
 *
 * Presses down to turn ON (sends savedBytes/rawBytes).
 * Releases to turn OFF (sends 0 to live hardware).
 *
 * Props:
 * comp           – full component object from play data
 * outputStates   – current output state map
 * onChange       – (mapping, value) callback
 * resolveImage   – image resolver function
 * highlightClass – CSS class string for step highlighting
 * style          – positioning style object
 */

const DUAL_PORT_MOTORS = [
    'mini_geared_motor',
    'geared_motor',
    'dc_motor',
    'dynamex_motor',
]

function LiveTriggerButtonControl({
    comp,
    outputStates,
    onChange,
    resolveImage,
    highlightClass,
    style,
    isLiveStep,
    onRequestActivate,
}) {
    const worker = useSelector((state) => state.worker)
    const webSerial = useSelector((state) => state.webSerial)

    const componentType = (comp.name || '').toLowerCase()
    const isDualPort = DUAL_PORT_MOTORS.includes(componentType)
    const port = comp.port

    // free=true → use fixedBytes (recorded); free=false → use direct (raw paste)
    const free = comp.properties.free ?? true
    const fixedBytes = comp.properties.fixedBytes || null
    const directBytes =
        comp.properties.direct && comp.properties.direct.length > 0
            ? comp.properties.direct
            : null
    const rawBytes = free ? null : directBytes
    const savedBytes = free ? fixedBytes : null

    const { port1, port2 } = useMemo(() => {
        if (!isDualPort || !port) return { port1: port, port2: null }
        const match = port.match(/^([A-Za-z]+)(\d+)$/)
        if (match) {
            const letter = match[1]
            const num = parseInt(match[2])
            return { port1: port, port2: `${letter}${num + 1}` }
        }
        return { port1: port + '1', port2: port + '2' }
    }, [port, isDualPort])

    const liveChange = useCallback(
        (targetPort, value) => {
            if (!worker || !webSerial?.isConnected) return
            const connectedDevice = sessionStorage.getItem('connectedDevice')
            if (!connectedDevice) return

            let adjustedValue = value
            if (componentType === 'servo_motor_270') {
                adjustedValue = Math.round(0.6667 * value)
            } else if (componentType === 'servo_motor_360') {
                adjustedValue = 100 + value
            }

            const charCode =
                compName_character[componentType] || 'P'.charCodeAt(0)
            const portNum =
                livePortsNumber?.[connectedDevice]?.[targetPort] ||
                livePortsNumber?.Ace?.[targetPort]
            if (portNum === undefined) return

            const data = [
                'L'.charCodeAt(0),
                'W'.charCodeAt(0),
                charCode,
                portNum,
                adjustedValue,
            ]
            worker.postMessage({ type: 'writeArray', value: data })
        },
        [componentType, worker, webSerial]
    )

    const sendLiveValue = useCallback(
        async (value) => {
            if (!port) return

            if (isDualPort) {
                if (value < 0) {
                    const absVal =
                        componentType === 'dynamex_motor'
                            ? Math.round(Math.abs(value) * 0.8)
                            : Math.abs(value)
                    liveChange(port1, absVal)
                    await new Promise((r) => setTimeout(r, 100))
                    liveChange(port2, 0)
                } else if (value > 0) {
                    const v =
                        componentType === 'dynamex_motor'
                            ? Math.round(value * 0.8)
                            : value
                    liveChange(port2, v)
                    await new Promise((r) => setTimeout(r, 100))
                    liveChange(port1, 0)
                } else {
                    liveChange(port2, 0)
                    await new Promise((r) => setTimeout(r, 100))
                    liveChange(port1, 0)
                }
            } else {
                liveChange(port, value)
            }
        },
        [port, isDualPort, port1, port2, liveChange, componentType]
    )

    const sendRawBytes = useCallback(async () => {
        if (!rawBytes || !worker || !webSerial?.isConnected) return
        worker.postMessage({ type: 'writeArray', value: rawBytes })
    }, [rawBytes, worker, webSerial])

    const sendSavedBytes = useCallback(async () => {
        if (!savedBytes || !worker || !webSerial?.isConnected) return
        for (const bytes of savedBytes) {
            worker.postMessage({ type: 'writeArray', value: bytes })
            await new Promise((r) => setTimeout(r, 100))
        }
    }, [savedBytes, worker, webSerial])

    const sendOffBytes = useCallback(async () => {
        if (!port || !worker || !webSerial?.isConnected) return
        await sendLiveValue(0)
    }, [port, worker, webSerial, sendLiveValue])

    // --- TRIGGER LOGIC (Nanosecond Pulse) ---
    const handlePress = useCallback(
        async (e) => {
            if (e) {
                e.preventDefault()
                e.stopPropagation()
            }
            if (onRequestActivate && !onRequestActivate()) return

            // 1. Immediately turn ON
            onChange(comp.mapping, 100)

            // 2. Send the ON signal (skip in live step — PlayScreen handles bytes)
            if (!isLiveStep) {
                if (rawBytes) {
                    await sendRawBytes()
                } else {
                    await sendSavedBytes()
                }
            }

            // 3. Turn OFF after short delay (visual flash + ensures signal sends)
            setTimeout(async () => {
                onChange(comp.mapping, 0)
                if (!isLiveStep && !rawBytes) {
                    await sendOffBytes()
                }
            }, 100)
        },
        [
            comp.mapping,
            onChange,
            isLiveStep,
            rawBytes,
            sendRawBytes,
            sendSavedBytes,
            sendOffBytes,
            onRequestActivate,
        ]
    )

    const isBtnOn = (outputStates[comp.mapping] || 0) !== 0
    const btnImg = isBtnOn
        ? comp.properties.onImage ||
          comp.properties.imageUrl ||
          "renderImage('gesture_Ac')"
        : comp.properties.offImage ||
          comp.properties.imageUrl ||
          "renderImage('gesture_IA')"

    return (
        <div
            key={comp.id}
            id={comp.id}
            className={`absolute-component playscreen-action-button${highlightClass}`}
            style={{ ...style, zIndex: style?.zIndex ?? 20 }}
        >
            <img
                className={comp.properties.className || ''}
                src={resolveImage(btnImg)}
                alt={comp.name}
                onPointerDown={handlePress}
                onDragStart={(e) => e.preventDefault()}
                style={{ cursor: 'pointer', touchAction: 'none' }}
            />
            {comp.properties.label && (
                <span className="control-label">{comp.properties.label}</span>
            )}
        </div>
    )
}

export default LiveTriggerButtonControl
